import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  styled,
  Tooltip,
  Typography,
} from '@mui/material'
import { filter, find, flatten, get, isEmpty, isNull, map } from 'lodash'
import PropTypes from 'prop-types'

import { PUBLIC_URL } from '../../common/constants'
import {
  CrackIcon,
  DentIcon,
  Dirt2Icon,
  MultipleDamageIcon,
  RepairFailedIcon,
  Scratch2Icon,
  SingleDamageIcon,
} from '../../icons'
import ImageCarousel from '../ImageCarousel'

const DAMAGE_ICONS = {
  Zabrudzenie: <Dirt2Icon />,
  Rysa: <Scratch2Icon />,
  Odkształcenie: <DentIcon />,
  Pęknięcie: <CrackIcon />,
  Uszkodzenie: <SingleDamageIcon />,
  'Inne uszkodzenia': <RepairFailedIcon />,
}

const StyledList = styled('ul')({
  listStyleType: 'none',
  margin: 0,
  padding: 0,
})

const StyledListItem = styled('li')({
  '&:before': {
    content: '"- "',
  },
})

const CarMeshImg = styled('img')({
  width: 260,
  height: 599,
})

const CarMeshPointer = styled('span')({
  position: 'absolute',
  zIndex: '100',
  cursor: 'pointer',
})

const CarMeshContainer = styled(Box)({
  position: 'relative',
  width: 260,
  height: 599,
})

function InteriorDamagesList() {
  const list = [
    { icon: <Dirt2Icon />, label: 'Zabrudzenie' },
    { icon: <Scratch2Icon />, label: 'Rysa' },
    { icon: <DentIcon />, label: 'Odkształcenie' },
    { icon: <CrackIcon />, label: 'Pęknięcie' },
    { icon: <RepairFailedIcon />, label: 'Uszkodzenie' },
    { icon: <SingleDamageIcon />, label: 'Inne uszkodzenie' },
    { icon: <MultipleDamageIcon />, label: 'Wiele uszkodzeń' },
  ]
  return (
    <List>
      {map(list, ({ icon, label }, key) => (
        <ListItem
          key={key}
          disablePadding
          sx={{
            padding: '0 !important',
            '&:before': { display: 'none !important' },
          }}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText>{label}</ListItemText>
        </ListItem>
      ))}
    </List>
  )
}

// eslint-disable-next-line react/prop-types
function InteriorTooltipDamages({ damages }) {
  return (
    <StyledList>
      {map(damages, (damage, key) => (
        <StyledListItem key={key}>{damage.interiorDamage}</StyledListItem>
      ))}
    </StyledList>
  )
}

// eslint-disable-next-line react/prop-types
function InteriorConditionMesh({ interiorConditions }) {
  const { t } = useTranslation()
  const elementsIconsCoords = [
    { interiorElementId: 1, coords: [150, 115] },
    { interiorElementId: 2, coords: [444, 115] },
    { interiorElementId: 3, coords: [270, 77] },
    { interiorElementId: 4, coords: [270, 170] },
    { interiorElementId: 5, coords: [380, 77] },
    { interiorElementId: 6, coords: [380, 160] },
    { interiorElementId: 7, coords: [260, 14] },
    { interiorElementId: 8, coords: [260, 224] },
    { interiorElementId: 9, coords: [370, 13] },
    { interiorElementId: 10, coords: [370, 224] },
    { interiorElementId: 11, coords: [214, 67] },
    { interiorElementId: 12, coords: [214, 168] },
    { interiorElementId: 13, coords: [335, 77] },
    { interiorElementId: 14, coords: [335, 163] },
    { interiorElementId: 15, coords: [163, 167] },
    { interiorElementId: 16, coords: [560, 115] },
    { interiorElementId: 17, coords: [208, 117] },
    { interiorElementId: 18, coords: [179, 67] },
  ]
  const interiorConditionsWithCoords = map(interiorConditions, (elem) => {
    const item = find(elementsIconsCoords, {
      interiorElementId: elem.interiorElementId,
    })
    if (item) {
      const mainDamage = find(elem.interiorDamages, { isMain: true }) || false
      return {
        ...elem,
        coords: item.coords,
        mainDamage,
      }
    }
    return elem
  })

  return (
    <CarMeshContainer>
      <CarMeshImg
        src={`${PUBLIC_URL}/assets/images/car_interior_mesh.png`}
        alt="carmesh"
      />
      {map(interiorConditionsWithCoords, (element, key) =>
        element.coords && element.mainDamage.interiorDamageId !== 1 ? (
          <CarMeshPointer
            key={key}
            sx={{ top: element.coords[0], left: element.coords[1] }}
            id={element.interiorElementId}
          >
            <Tooltip
              title={
                <>
                  {t('damagesTooltipHead', {
                    element: element.interiorElementName,
                  })}
                  <InteriorTooltipDamages damages={element.interiorDamages} />
                </>
              }
              arrow
            >
              <Box component="span">
                {element.interiorDamages.length > 1 ? (
                  <MultipleDamageIcon />
                ) : (
                  get(DAMAGE_ICONS, element.mainDamage.interiorDamage)
                )}
              </Box>
            </Tooltip>
          </CarMeshPointer>
        ) : null,
      )}
    </CarMeshContainer>
  )
}

function DigitalCertificateInteriorCondition({ certificateData, index }) {
  const { t } = useTranslation()
  const { basicData, interiorConditions } = certificateData
  const images = filter(
    flatten(
      map(interiorConditions, ({ interiorDamages, interiorElementName }) =>
        map(interiorDamages, (damage) =>
          isNull(damage.image)
            ? null
            : {
                url: damage.image.imageUrl,
                name: `${interiorElementName} - ${damage.interiorDamage}`,
              },
        ),
      ),
    ),
    (el) => !isNull(el),
  )

  return (
    <Grid container spacing={5} sx={{ mt: 0 }}>
      <Grid item xs={12} md={6}>
        <Paper elevation={0} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={index ? 12 : 4}>
              <Typography variant="h4">
                {t('digital.interior.legend')}
              </Typography>
              <InteriorDamagesList />
            </Grid>
            <Grid item xs={12} lg={index ? 12 : 8}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <InteriorConditionMesh
                  interiorConditions={interiorConditions}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        {isEmpty(images) ? (
          <img src={basicData.imageUrl} alt="" style={{ width: '100%' }} />
        ) : (
          <ImageCarousel images={images} />
        )}
      </Grid>
    </Grid>
  )
}

DigitalCertificateInteriorCondition.propTypes = {
  certificateData: PropTypes.object,
  index: PropTypes.bool,
}

DigitalCertificateInteriorCondition.defaultProps = {
  certificateData: null,
  index: false,
}

export default DigitalCertificateInteriorCondition
